import React, { useState, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Filter, Search, Eye, X, Calendar, Pencil } from 'lucide-react';
import EditApplicationModal from '../../../pages/Admin/Applications/components/EditApplicationModal';
import AddApplicationModal from '../../../pages/Admin/Applications/components/AddApplicationModal';
import EnhancedApplicationsTable from './EnhancedApplicationsTable';
import { TableColumn, ColumnWidth } from '../../../components/common/EnhancedTable/types';
import './EnhancedApplications.css';
import { getLeaseApplications } from '../../../services/supabase/lease-applications';
import type { LeaseApplication } from '../../../types/lease-application';
import type { SortConfig } from '../../../types/lease';
import { formatTextWithLinks } from '../../../utils/formatters';
import { usePermissions } from '../../../hooks/usePermissions';
import { useFilteredAndSortedApplications } from '../../../hooks/useFilteredAndSortedApplications';
import { format } from 'date-fns';
import LeaseApplicationDetailsModal from '../../../components/LeaseApplications/LeaseApplicationDetailsModal';

// Define the application filter type
type ApplicationFilter = {
  status: string | null;
  dateFrom: Date | null;
  dateTo: Date | null;
};

// Define the sort field type
type ApplicationSortField = 'app_id' | 'app_lot' | 'app_type' | 'app_date' | 'app_approv_date' | 'status';

export default function EnhancedApplications() {
  const { canManageSystemSettings, isAdmin } = usePermissions();
  const queryClient = useQueryClient();
  
  // State for modals
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editApplication, setEditApplication] = useState<LeaseApplication | null>(null);
  
  // State for filters, sorting, and pagination
  const [searchQuery, setSearchQuery] = useState('');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [filters, setFilters] = useState<ApplicationFilter>({
    status: null,
    dateFrom: null,
    dateTo: null
  });
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    field: 'app_date',
    direction: 'desc'
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  
  // State for modals
  const [selectedApplication, setSelectedApplication] = useState<LeaseApplication | null>(null);

  // Fetch application data
  const { data: applications, isLoading, error } = useQuery({
    queryKey: ['leaseApplications'],
    queryFn: getLeaseApplications
  });

  // Apply filters and sorting
  const filteredAndSortedApplications = useFilteredAndSortedApplications(
    applications,
    filters,
    sortConfig,
    searchQuery
  );

  // Apply pagination
  const paginatedApplications = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return filteredAndSortedApplications.slice(startIndex, endIndex);
  }, [filteredAndSortedApplications, currentPage, pageSize]);

  // Handle sorting
  const handleSort = (sortConfig: { field: string; direction: 'asc' | 'desc' }) => {
    console.log('Sorting:', sortConfig);
    setSortConfig(sortConfig as SortConfig);
  };

  // Handle sort button clicks
  const handleSortButtonClick = (field: ApplicationSortField) => {
    setSortConfig(current => ({
      field,
      direction: current.field === field && current.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Handle filter changes
  const handleFilterChange = (newFilters: Partial<ApplicationFilter>) => {
    setFilters(current => ({ ...current, ...newFilters }));
  };

  // Handle edit application
  const handleEditClick = (application: LeaseApplication) => {
    setEditApplication(application);
    setIsEditModalOpen(true);
  };

  // Handle edit success
  const handleEditSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['leaseApplications'] });
  };

  // Handle view details
  const handleViewDetails = (application: LeaseApplication) => {
    setSelectedApplication(application);
  };

  // Handle remove filter
  const handleRemoveFilter = (key: keyof ApplicationFilter) => {
    setFilters(prev => ({ ...prev, [key]: null }));
  };

  // Handle pagination
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(1); // Reset to first page when changing page size
  };

  // Calculate pagination configuration
  const paginationConfig = {
    currentPage,
    pageSize,
    totalCount: filteredAndSortedApplications.length,
    onPageChange: handlePageChange,
    onPageSizeChange: handlePageSizeChange,
    pageSizeOptions: [25, 50, 100, 200]
  };

  // Define table columns
  const columns: TableColumn<LeaseApplication>[] = [
    {
      id: 'edit',
      header: 'Edit',
      accessor: (row) => (
        <div className="action-buttons">
          <button
            onClick={() => handleEditClick(row)}
            className="action-button edit"
            title="Edit Application"
          >
            <Pencil className="h-5 w-5" />
          </button>
        </div>
      ),
      width: 'narrow' as ColumnWidth,
      visibleWhen: (permissions) => permissions.isAdmin || permissions.canManageSystemSettings
    },
    { 
      id: 'view', 
      header: 'View', 
      accessor: (row) => (
        <button
          onClick={() => handleViewDetails(row)}
          className="w-10 h-10 flex items-center justify-center text-primary hover:text-primary-dark bg-blue-50 hover:bg-blue-100 rounded-lg"
          title="View Details"
        >
          <Eye className="h-5 w-5" />
        </button>
      ),
      width: 'narrow' as ColumnWidth,
      align: 'center' as 'center'
    },
    {
      id: 'app_lot',
      header: 'Lot Number',
      accessor: (row) => (
        <div className="truncate" title={row.app_lot || '-'}>
          {formatTextWithLinks(row.app_lot || '-')}
        </div>
      ),
      width: 'medium' as ColumnWidth,
      sortable: true
    },
    {
      id: 'app_type',
      header: 'Type',
      accessor: (row) => (
        <div className="truncate" title={row.app_type || '-'}>
          {formatTextWithLinks(row.app_type || '-')}
        </div>
      ),
      width: 'medium' as ColumnWidth,
      sortable: true
    },
    {
      id: 'app_date',
      header: 'Application Date',
      accessor: (row) => (
        <div className="truncate">
          {format(new Date(row.app_date), 'dd/MM/yyyy')}
        </div>
      ),
      width: 'medium' as ColumnWidth,
      sortable: true
    },
    {
      id: 'app_approv_date',
      header: 'Approval Date',
      accessor: (row) => (
        <div className="truncate">
          {row.app_approv_date 
            ? format(new Date(row.app_approv_date), 'dd/MM/yyyy')
            : '-'}
        </div>
      ),
      width: 'medium' as ColumnWidth,
      sortable: true
    },
    {
      id: 'status',
      header: 'Status',
      accessor: (row) => {
        let statusClass = 'pending';
        let statusText = 'Pending';
        
        if (row.app_approv_date) {
          statusClass = 'approved';
          statusText = 'Approved';
        } else if (row.app_final === 'rejected') {
          statusClass = 'rejected';
          statusText = 'Rejected';
        }
        
        return (
          <span className={`status-badge ${statusClass}`}>
            {statusText}
          </span>
        );
      },
      width: 'medium' as ColumnWidth,
      sortable: true
    },
    {
      id: 'app_desc',
      header: 'Description',
      accessor: (row) => (
        <div className="truncate" title={row.app_desc || '-'}>
          {formatTextWithLinks(row.app_desc || '-')}
        </div>
      ),
      width: 'wide' as ColumnWidth
    },
    {
      id: 'app_id',
      header: 'ID',
      accessor: (row) => row.app_id,
      width: 'narrow' as ColumnWidth,
      sortable: true
    }
  ];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-800"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 text-red-500">
        Error loading application data. Please try again later.
      </div>
    );
  }

  return (
    <div className="enhanced-applications-container p-4">
      <div className="enhanced-applications-header">
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h1 className="text-2xl font-bold">Lease Applications</h1>
            
            {/* Add button - only visible to users with admin privileges */}
            {(isAdmin || canManageSystemSettings) && (
              <button 
                onClick={() => setIsAddModalOpen(true)}
                className="flex items-center px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark"
              >
                <Plus className="h-5 w-5 mr-2" />
                Add Application
              </button>
            )}
          </div>

          <div className="flex items-center gap-2">
            {/* Search input */}
            <div className="relative flex-1 max-w-md">
              <input
                type="text"
                placeholder="Search applications..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
              />
              <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
            </div>
            
            {/* Filter button */}
            <button
              className="p-2 border border-gray-300 rounded-md hover:bg-gray-50 flex items-center gap-1"
              onClick={() => setIsFilterOpen(!isFilterOpen)}
              title="Toggle filters"
            >
              <Filter className="h-5 w-5 text-gray-500" />
              <span className="hidden sm:inline">Filters</span>
              {Object.values(filters).filter(Boolean).length > 0 && (
                <span className="ml-1 bg-primary text-white text-xs rounded-full px-2 py-0.5">
                  {Object.values(filters).filter(Boolean).length}
                </span>
              )}
            </button>
            
            <div className="text-base font-bold text-gray-700">
              Total Applications: {filteredAndSortedApplications.length}
            </div>
          </div>

          {/* Active filters */}
          {Object.values(filters).some(Boolean) && (
            <div className="active-filters">
              {filters.status && (
                <div className="filter-tag">
                  <span className="filter-tag-label">Status:</span>
                  <span>{filters.status === 'approved' ? 'Approved' : filters.status === 'rejected' ? 'Rejected' : 'Pending'}</span>
                  <button
                    onClick={() => handleRemoveFilter('status')}
                    className="filter-tag-remove"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              )}
              {filters.dateFrom && (
                <div className="filter-tag">
                  <span className="filter-tag-label">From:</span>
                  <span>{format(filters.dateFrom, 'dd/MM/yyyy')}</span>
                  <button
                    onClick={() => handleRemoveFilter('dateFrom')}
                    className="filter-tag-remove"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              )}
              {filters.dateTo && (
                <div className="filter-tag">
                  <span className="filter-tag-label">To:</span>
                  <span>{format(filters.dateTo, 'dd/MM/yyyy')}</span>
                  <button
                    onClick={() => handleRemoveFilter('dateTo')}
                    className="filter-tag-remove"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Filter panel */}
          {isFilterOpen && (
            <div className="filter-panel">
              <div className="filter-panel-header">
                <h3 className="font-medium text-gray-700">Filters</h3>
                <button
                  onClick={() => setFilters({
                    status: null,
                    dateFrom: null,
                    dateTo: null
                  })}
                  className="text-sm text-blue-600 hover:text-blue-800"
                >
                  Clear all
                </button>
              </div>
              <div className="filter-panel-body">
                {/* Status filter */}
                <div className="filter-group">
                  <label htmlFor="status-filter">Status</label>
                  <select
                    id="status-filter"
                    value={filters.status || ''}
                    onChange={(e) => handleFilterChange({ status: e.target.value || null })}
                    className="border border-gray-300 rounded-md p-2 text-sm"
                  >
                    <option value="">All statuses</option>
                    <option value="pending">Pending</option>
                    <option value="approved">Approved</option>
                    <option value="rejected">Rejected</option>
                  </select>
                </div>
                
                {/* Date range filters */}
                <div className="filter-group">
                  <label htmlFor="date-from-filter">Date From</label>
                  <input
                    id="date-from-filter"
                    type="date"
                    value={filters.dateFrom?.toISOString().split('T')[0] || ''}
                    onChange={(e) => handleFilterChange({ 
                      dateFrom: e.target.value ? new Date(e.target.value) : null 
                    })}
                    className="border border-gray-300 rounded-md p-2 text-sm"
                  />
                </div>
                
                <div className="filter-group">
                  <label htmlFor="date-to-filter">Date To</label>
                  <input
                    id="date-to-filter"
                    type="date"
                    value={filters.dateTo?.toISOString().split('T')[0] || ''}
                    onChange={(e) => handleFilterChange({ 
                      dateTo: e.target.value ? new Date(e.target.value) : null 
                    })}
                    className="border border-gray-300 rounded-md p-2 text-sm"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Sort controls removed - now using column headers for sorting */}
        </div>
      </div>
      
      <div className="enhanced-applications-table-container enhanced-table-force-scroll">
        <EnhancedApplicationsTable
          columns={columns}
          data={paginatedApplications}
          keyField="app_id"
          width="1800px"
          maxHeight="calc(100vh - 20rem)"
          loading={isLoading}
          emptyMessage="No applications found"
          horizontalScroll={true}
          alternateRowColors={true}
          pagination={paginationConfig}
          defaultSort={sortConfig}
          onSort={handleSort}
          permissions={{
            isAdmin,
            canManageSystemSettings
          }}
        />
      </div>
      
      {/* Application details modal */}
      {selectedApplication && (
        <LeaseApplicationDetailsModal
          application={selectedApplication}
          onClose={() => setSelectedApplication(null)}
        />
      )}

      {/* Add Application Modal */}
      {isAddModalOpen && (
        <AddApplicationModal
          isOpen={true}
          onClose={() => setIsAddModalOpen(false)}
          onSuccess={() => {
            handleEditSuccess();
            setIsAddModalOpen(false);
          }}
        />
      )}

      {/* Edit Application Modal */}
      {editApplication && (
        <EditApplicationModal
          isOpen={isEditModalOpen}
          onClose={() => {
            setIsEditModalOpen(false);
            setEditApplication(null); // Reset selected application when modal is closed
          }}
          onSuccess={handleEditSuccess}
          application={editApplication}
        />
      )}
    </div>
  );
}
